import Form from '@components/Form'
import { Mode } from '@features/fields/components/PlanningPane/BoundaryOptions'
import { ConfirmAdditionalBoundaryModal } from '@features/fields/components/PlanningPane/ConfirmAdditionalBoundaryModal'
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  ACCEPTED_FILE_EXTENSIONS_FOR_INPUT,
  SHAPEFILE_EXTENSIONS,
  stripFileExtension
} from '@lib/GeospatialFile'
import { CropType, resolveCropKey } from 'api-validations/ag-concepts'
import { ImportSurface, importFileAccept } from 'api-validations/data-import'
import {
  type AllBoundariesResponseType,
  type AllPrescriptionsResponseType,
  baseCffSchema,
  convertFileToCffAPI,
  createPlanningCffsApi,
  deleteBoundaryApi,
  type FrontendPlan,
  GeoSpatialFileType,
  getAllBoundariesApi,
  getAllPrescriptionsApi,
  getInitialPlan,
  type Locutus,
  locutusCreateBoundaryApi,
  PlanAssetCreateError
} from 'api-validations/locutus'
import { GuidancePrimitive } from 'api-validations/locutus/guidance'
import { ExportType, type Task, taskSchema } from 'api-validations/pangea'
import { type ControllableApplication } from 'api-validations/stitch'
import { type Dispatch, Fragment, ReactNode, type SetStateAction, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useSearchParams } from 'react-router'
import { OrgLevelPermissions, useOrgCodeOrThrow, useOrgInfo } from 'shared-ui/active-operation'
import { usePanoramaMutation, usePanoramaQuery } from 'shared-ui/api-provider'
import {
  BoundaryIcon,
  ButtonKind,
  ChevronDownIcon,
  ClientIcon,
  CombineIcon,
  type DisabledGuard,
  FarmIcon,
  HybridSolidIcon,
  Icon,
  Loading,
  MeatballMenu,
  MeatballMenuItem,
  Modal,
  PanoramaButton as Button,
  PanoramaSelectMenu,
  PlanterIcon,
  PlanningFieldsIcon,
  Popover,
  PrescriptionIcon,
  sectionCardClasses,
  SidedressIcon,
  SprayerIcon,
  StripTillIcon,
  TankLevelIcon,
  TaskIcon,
  TextButton
} from 'shared-ui/components'
import { FeatureFlag, useFeatureFlag } from 'shared-ui/debug'
import { SpriteMap } from 'shared-ui/global'
import { createVariants } from 'shared-ui/lib'
import { useLocalization } from 'shared-ui/localization'
import {
  type Cff,
  browsableFields,
  cffKey,
  translateCropKey,
  useAllStreams,
  useCffConfigFileDownload
} from 'shared-ui/operation-wide-data'
import { useSyncOnLayoutChange } from 'shared-ui/util-hooks'
import { groupAndReduce, isNil, isNonNil, numsFromTo } from 'utils'
import { z } from 'zod'

import {
  ModalActionOption,
  PlanningModalsProvider,
  usePlanningModalsContext,
  usePlanningModalsDispatchContext
} from '../../../../contexts/PlanningModalsContext'
import { SELECT_UNREGISTERED_FIELDS_PARAM } from '../../../../routes/paths'
import { useCreatePlan } from '../../api/useCreatePlan'
import { useDeletePlanningCffs } from '../../api/useDeletePlanningCffs'
import { useDeletePlans } from '../../api/useDeletePlans'
import {
  type PlanningClient,
  usePlanningCffItems,
  usePlanningCffs
} from '../../api/usePlanningCffs'
import { useUpdatePlan } from '../../api/useUpdatePlan'
import {
  type ImportedPrescription,
  PlanAssetUpload
} from '../../components/PlanAssetUpload/PlanAssetUpload'
import { DestinationKind } from '../../lib/planUpload/planUploadReducer'

const CFF_ACCEPTED_FILE_EXTENSIONS = importFileAccept(ImportSurface.FieldList)

/**
 * The top of OperationLayout (the nearest ancestor with overflow-y) touches the bottom of the nav bar and has a
 * top padding of 24px. For our sticky headers to come up to the nav bar, their tops needs to be 24px above
 * the top of the OperationLayout content area.
 */
const OPERATION_LAYOUT_CONTENT_TO_NAV_BAR_PX = -24
const CLIENT_HEADER_HEIGHT_PX = 64
/**
 * The height from the bottom of the nav bar to the bottom of the Client header
 * (accounting for the top padding of OperationLayout)
 */
const BOUNDING_TOP_PX = OPERATION_LAYOUT_CONTENT_TO_NAV_BAR_PX + CLIENT_HEADER_HEIGHT_PX
const FARM_HEADER_HEIGHT_PX = 32
const FIELD_HEADER_HEIGHT_PX = 40
/**
 * Geometry for the client→farm connector "L", whose horizontal segment is resized on
 * scroll so its tip always lands on the nearest farm icon's circular border. When no
 * icon is level with it, it lands on the vertical line that runs between the farm icons.
 * - MAX_WIDTH: distance from the connector's left (x=32) to the farm center line (x=80).
 * - ICON_RADIUS: half of the farm icon's `size-8` (32px) circle. At a vertical offset
 *   `dy` from the icon center, the circle's near edge is `sqrt(R^2 - dy^2)` left of the
 *   center line, so the horizontal must stop that far short of MAX_WIDTH.
 */
const CLIENT_CONNECTOR_MAX_WIDTH_PX = 48
const FARM_ICON_RADIUS_PX = 16

// TODO: Refactor

type CffDeletion =
  | {
      type: 'client'
      client: PlanningClient
    }
  | {
      type: 'farm'
      farm: PlanningClient['farms'][0]
    }
  | {
      type: 'field'
      field: PlanningClient['farms'][0]['fields'][0]
    }

type DeleteCff = (cff: CffDeletion) => void

type OpenCffModal = (opts: { client?: string; farm?: string }) => void

export type MonthAndYear = { month: number; year: number }

export type PlanAssetChipType = {
  id: string
  name: string
  type: keyof typeof planAssetChipConfig
}

export type FilePlanAssetModalType = Mode.Boundary | Mode.Prescription | undefined

const planCardClasses = createVariants({
  base: 'relative flex min-w-0 flex-col gap-y-3 p-4 group-last/plan:rounded-b-lg',
  variants: {
    mapPage: { true: 'rounded-lg bg-gray-50', false: 'bg-white' },
    disabled: { true: 'opacity-50' }
  }
})

const addFarmFieldButtonClasses = createVariants({
  base: 'pan-title5 flex shrink-0 items-center whitespace-nowrap py-1 disabled:opacity-50 disabled:cursor-not-allowed'
})

// Rendered as a label for the hidden file input, so it has no native disabled state to style.
const importCffButtonClasses = createVariants({
  base: 'grow cursor-pointer',
  variants: {
    disabled: {
      true: 'pointer-events-none opacity-50',
      false: ''
    }
  }
})

/** Tailwind's `md` breakpoint; below it tree headers stack, so they only stick from here up. */
const TREE_ROW_LAYOUT_MEDIA_QUERY = '(min-width: 768px)'

const treeHeaderClasses = 'flex w-full min-w-0 items-start gap-x-2 md:items-center'
const treeHeaderBodyClasses =
  'flex min-w-0 flex-1 flex-col gap-y-1 bg-gray-50 md:flex-row md:items-center md:justify-between md:gap-y-0 md:bg-transparent'
const treeHeaderTitleClasses = 'flex min-w-0 items-center gap-x-2'
const treeHeaderActionsClasses = 'flex flex-wrap items-center gap-x-2'
const treeNameClasses = 'min-w-0 break-words'

const farmTreeSpineClasses = createVariants({
  base: 'pointer-events-none absolute left-4 border-l border-green md:hidden',
  variants: {
    position: {
      first: 'bottom-0 top-4',
      middle: 'inset-y-0',
      last: 'top-0 h-4'
    }
  }
})

const farmTreeSpinePosition = ({
  farmIndex,
  farmCount
}: {
  farmIndex: number
  farmCount: number
}) => {
  if (farmIndex === 0) return 'first'
  if (farmIndex === farmCount - 1) return 'last'
  return 'middle'
}

const FarmTreeSpine = ({ farmIndex, farmCount }: { farmIndex: number; farmCount: number }) => {
  if (farmCount <= 1) return null
  const position = farmTreeSpinePosition({ farmIndex, farmCount })
  return (
    <>
      {position !== 'first' && (
        <div className="pointer-events-none absolute -top-4 left-4 h-4 border-l border-green md:hidden" />
      )}
      <div className={farmTreeSpineClasses({ position })} />
    </>
  )
}

const disclosureChevronClasses = createVariants({
  base: 'shrink-0 group-data-[open]:rotate-0 -rotate-90 transition-transform'
})

const iconBorderClasses = createVariants({
  base: 'mr-1 flex size-8 shrink-0 items-center justify-center rounded-full border border-green bg-white'
})

const planEditingHeaderClasses = createVariants({
  base: 'z-[1] flex items-center justify-between bg-green px-4 py-1 text-white',
  variants: {
    sticky: {
      true: 'md:sticky',
      false: ''
    }
  }
})

const CropChipLabel = () => {
  const { t } = useLocalization()
  return <span className="text-white">{t(($) => $.agConcepts.crop)}</span>
}

const planAssetChipConfig = {
  boundary: {
    icon: <BoundaryIcon />,
    background: 'bg-accent-400',
    border: 'border-accent-400'
  },
  prescription: {
    icon: <PrescriptionIcon />,
    background: 'bg-violet-400',
    border: 'border-violet-400'
  },
  crop: {
    icon: <CropChipLabel />,
    background: 'bg-green',
    border: 'border-green'
  },
  liquidTankMix: {
    icon: <TankLevelIcon />,
    background: 'bg-blue-500',
    border: 'border-blue-500'
  },
  granularTankMix: {
    icon: <TankLevelIcon />,
    background: 'bg-orange-700',
    border: 'border-orange-700'
  },
  hybrid: {
    icon: <HybridSolidIcon />,
    background: 'bg-lime-600',
    border: 'border-lime-600'
  },
  guidance: {
    icon: <Icon icon={SpriteMap.GuidanceIcon} className="size-4 text-white" />,
    background: 'bg-lime-600',
    border: 'border-lime-600'
  }
} as const

export const taskIcons: Record<Task, ReactNode> = {
  plant: <PlanterIcon color="text-inherit" />,
  sidedress: <SidedressIcon color="text-inherit" />,
  spray: <SprayerIcon color="text-inherit" />,
  striptill: <StripTillIcon color="text-inherit" />,
  harvest: <CombineIcon color="text-inherit" />
}

export const PlanAssetChip = ({
  planAsset,
  onRemove
}: {
  planAsset: PlanAssetChipType
  onRemove?: Dispatch<SetStateAction<any>>
}) => {
  const { t } = useLocalization()
  return (
    <div className="inline-flex min-h-6 w-max max-w-full items-stretch">
      <div
        className={`flex shrink-0 items-center rounded-l-full pl-2 pr-1 ${planAssetChipConfig[planAsset.type].background}`}
      >
        {planAssetChipConfig[planAsset.type].icon}
      </div>
      <div
        className={`flex min-w-0 items-center rounded-r-full border px-2 ${planAssetChipConfig[planAsset.type].border}`}
      >
        <span className="min-w-0 break-words leading-4">{planAsset.name}</span>
        {isNonNil(onRemove) && (
          <button
            type="button"
            aria-label={t(($) => $.buttons.remove)}
            className="ml-2.5 shrink-0"
            onClick={onRemove}
          >
            <Icon icon={SpriteMap.CloseCircleIcon} className="size-4" />
          </button>
        )}
      </div>
    </div>
  )
}

export const FilePlanAssetUploader = ({
  fieldId,
  processSavedAssets,
  modalState,
  saveBoundaryOnChange
}: {
  fieldId: string
  processSavedAssets: (
    assets: {
      id: string
      name: string
      type?: ControllableApplication | GuidancePrimitive
    }[]
  ) => void
  modalState: [FilePlanAssetModalType, Dispatch<SetStateAction<FilePlanAssetModalType>>]
  saveBoundaryOnChange?: true
}) => {
  const { t } = useLocalization()
  const [assetType, setAssetType] = modalState
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  // const [uploadedFilesByName, setUploadedFilesByName] = useState<
  //   Record<string, File[]>
  // >({})
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([])
  const [assetName, setAssetName] = useState('')

  const [selectedExistingAsset, setSelectedExistingAsset] = useState<
    | AllBoundariesResponseType['items'][number]
    | AllPrescriptionsResponseType['items'][number]
    | undefined
  >()

  const [showBoundaryConfirm, setShowBoundaryConfirm] = useState(false)
  const [pendingFiles, setPendingFiles] = useState<File[] | undefined>()

  const orgCode = useOrgCodeOrThrow()
  const createBoundaryAsset = usePanoramaMutation(locutusCreateBoundaryApi)
  const deleteBoundaryAsset = usePanoramaMutation(deleteBoundaryApi)
  const existingBoundariesForField = usePanoramaQuery(
    getAllBoundariesApi,
    { orgCode },
    { select: ({ items }) => items.filter((item) => item.fieldId === fieldId) }
  )
  const existingPrescriptionsForField = usePanoramaQuery(
    getAllPrescriptionsApi,
    { orgCode },
    { select: ({ items }) => items.filter((item) => item.fieldId === fieldId) }
  )

  // TODO: Create a form(?) or some way to keep track of all files, their new given name (if there is one), their measure system, and their application type

  // const uploadedFilesNames = Object.keys(uploadedFilesByName)

  if (isNil(assetType)) return

  const closeFilePlanAssetModal = () => {
    setAssetType(undefined)
    setErrorMessage(null)
    setUploadedFiles([])
    setAssetName('')
    setSelectedExistingAsset(undefined)
    setShowBoundaryConfirm(false)
    setPendingFiles(undefined)
    createBoundaryAsset.reset()
    deleteBoundaryAsset.reset()
  }

  const processUploadedFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    // This is to clear any errors the mutation may have thrown if a subsequent file(s) is uploaded
    createBoundaryAsset.reset()
    const files = Array.from(e.target.files ?? [])
    if (files.length === 0) return
    const hasSingleFileAsset = files.some(
      ({ name }) => name.endsWith('.2020') || name.toLowerCase().endsWith('.zip')
    )

    const firstFileNameWithoutExtension = stripFileExtension(files[0].name)

    if (hasSingleFileAsset && files.length > 1)
      setErrorMessage(t(($) => $.planning[`onlyOne${assetType}`]))
    else if (!files.every(({ name }) => stripFileExtension(name) === firstFileNameWithoutExtension))
      setErrorMessage(t(($) => $.planning.filesMustHaveSameName))
    else if (
      hasSingleFileAsset ||
      SHAPEFILE_EXTENSIONS.every(
        (extension) =>
          extension === 'dbf' ||
          files.some((file) => file.name.toLowerCase().endsWith(`.${extension}`))
      )
    ) {
      setUploadedFiles(files)
      setErrorMessage(null)
      if (saveBoundaryOnChange === true) confirmThenSave(files)
    } else setErrorMessage('planning.uploadValidShapefile')

    // TODO: Replace above single-asset logic with below multi-asset logic
    // // TODO: In case of boundaries, throw validation error if there are two different file names (since we can only have one boundary)
    // const filesByName: Record<string, File[]> = {}
    // for (const file of files) {
    //   const nameWithoutExtension = file.name.slice(
    //     0,
    //     files[0].name.lastIndexOf('.')
    //   )
    //   const extension = file.name.slice(files[0].name.lastIndexOf('.') + 1)
    //   if (!ACCEPTED_FILE_EXTENSIONS.some((ext) => ext === extension)) continue
    //   if (nameWithoutExtension in filesByName)
    //     filesByName[nameWithoutExtension].push(file)
    //   else filesByName[nameWithoutExtension] = [file]
    // }
    // const numFiles = Object.keys(filesByName).length
    // // TODO: Handle this in next step (make user remove the files they don't want)?
    // // TODO: File validation. If there's a .2020 file, it should be the only file. If there's a .shp|.shx, both should be there. If it's a prescription and a .shp|.shx, there needs to also be a .dbf
    // if (
    //   assetType === Mode.Boundary &&
    //   numFiles > 1
    // )
    //   setErrorMessage('A plan can only have one boundary (.2020 or shapefile)')
    // else if (numFiles === 0)
    //   setErrorMessage('Please upload one or more valid .2020 or shapefiles')
    // else setUploadedFilesByName(filesByName)
  }

  const saveAssets = (files?: File[]) => {
    const resolvedFiles = files ?? uploadedFiles
    if (isNonNil(selectedExistingAsset)) {
      processSavedAssets([
        {
          ...selectedExistingAsset,
          type:
            'applicationType' in selectedExistingAsset
              ? selectedExistingAsset.applicationType
              : undefined
        }
      ])
      closeFilePlanAssetModal()
      return
    }

    // TODO: Once we switch to multiple assets uploaded at a time, everything should be validated by now so that we can blindly create all the plan assets
    const [dbf, shp, shx] = SHAPEFILE_EXTENSIONS.map((extension) =>
      resolvedFiles.find(({ name }) => name.toLowerCase().endsWith(`.${extension}`))
    )
    const is2020 = resolvedFiles[0].name.endsWith('.2020')
    const isZip = resolvedFiles[0].name.toLowerCase().endsWith('.zip')

    const mutationOptions = {
      onSuccess: (filePlanAssets: z.infer<typeof locutusCreateBoundaryApi.responseSchema>) => {
        processSavedAssets(filePlanAssets.items)
        closeFilePlanAssetModal()
      },
      onError: () => {
        ;(document.getElementById('geospatial-file') as HTMLInputElement).value = ''
        // setUploadedFilesByName({})
        setUploadedFiles([])
      }
    }

    const sharedAttributes = {
      fieldId,
      name: assetName === '' ? stripFileExtension(resolvedFiles[0].name) : assetName
    }
    if (is2020)
      createBoundaryAsset.mutate(
        {
          params: { orgCode },
          body: {
            ...sharedAttributes,
            type: GeoSpatialFileType.Gen3_2020,
            file: resolvedFiles[0]
          }
        },
        mutationOptions
      )
    else if (isZip)
      createBoundaryAsset.mutate(
        {
          params: { orgCode },
          body: {
            ...sharedAttributes,
            type: GeoSpatialFileType.ShapeFile,
            zip: resolvedFiles[0]
          }
        },
        mutationOptions
      )
    else if (isNonNil(shp) && isNonNil(shx))
      createBoundaryAsset.mutate(
        {
          params: { orgCode },
          body: {
            ...sharedAttributes,
            type: GeoSpatialFileType.ShapeFile,
            dbf,
            shp,
            shx
          }
        },
        mutationOptions
      )
  }

  // Boundaries get an extra confirmation step when the field already has one, so
  // a second boundary is never created by accident. Importing an existing asset
  // (rather than uploading) and non-boundary assets bypass this.
  const confirmThenSave = (files?: File[]) => {
    if (
      assetType === Mode.Boundary &&
      isNil(selectedExistingAsset) &&
      (existingBoundariesForField.data?.length ?? 0) > 0
    ) {
      setPendingFiles(files)
      setShowBoundaryConfirm(true)
    } else saveAssets(files)
  }

  const replaceBoundaryThenSave = async () => {
    try {
      await Promise.all(
        (existingBoundariesForField.data ?? []).map((b) =>
          deleteBoundaryAsset.mutateAsync({ params: { orgCode, boundaryId: b.id } })
        )
      )
    } catch {
      // Surfaced via deleteBoundaryAsset.isError in the modal; keep it open so
      // the user can retry rather than silently dropping the failure.
      return
    }
    setShowBoundaryConfirm(false)
    saveAssets(pendingFiles)
  }

  // errorMap turns a 409 into a typed DuplicateName so we can localize it, while
  // other failures keep their server-provided text.
  const createAssetError = createBoundaryAsset.error
  const createAssetErrorMessage = isNil(createAssetError)
    ? undefined
    : createAssetError.type === PlanAssetCreateError.DuplicateName
      ? t(($) => $.planning.duplicateAssetName.Boundary)
      : createAssetError.type === PlanAssetCreateError.TooLarge
        ? t(($) => $.planning.assetTooLargeToSave)
        : createAssetError.message || t(($) => $.messages.errorOccurredTitle)

  const existingAssets = {
    [Mode.Boundary]: existingBoundariesForField.data ?? [],
    [Mode.Prescription]: existingPrescriptionsForField.data ?? []
  }[assetType]
  const hasExistingAssetsForSelectedType = existingAssets.length > 0

  const existingAssetPicker = (
    <PanoramaSelectMenu
      label={t(($) => $.planning.selectFromExisting)}
      options={existingAssets}
      value={selectedExistingAsset}
      onChange={setSelectedExistingAsset}
      placeholder={
        hasExistingAssetsForSelectedType
          ? t(($) => $.planning[`add${assetType}`].selectPlaceholder)
          : t(($) => $.planning[`add${assetType}`].noneExisting)
      }
      disabled={!hasExistingAssetsForSelectedType}
      clearable
    />
  )

  const offersSave = assetType === Mode.Boundary || isNonNil(selectedExistingAsset)

  const importPrescriptions = ({
    created,
    failed
  }: {
    created: readonly ImportedPrescription[]
    failed: number
  }) => {
    if (created.length > 0) processSavedAssets([...created])
    if (failed === 0) closeFilePlanAssetModal()
  }

  return (
    <Modal
      title={
        <span className="flex items-center gap-x-2">
          {assetType === Mode.Prescription ? (
            <PrescriptionIcon color="text-green" />
          ) : (
            <BoundaryIcon color="text-green" />
          )}
          {t(($) => $.planning[`add${assetType}`].title)}
        </span>
      }
      description={t(($) => $.planning[`add${assetType}`].description)}
      show={isNonNil(assetType)}
      onClose={closeFilePlanAssetModal}
      size={assetType === Mode.Prescription ? 'xl' : 'lg'}
    >
      <div className="mt-4">
        {assetType === Mode.Prescription ? (
          <div className="flex flex-col items-center gap-y-4 rounded-lg border p-4">
            {existingAssetPicker}
            {t(($) => $.or)}
            <PlanAssetUpload
              destination={DestinationKind.Field}
              fieldId={fieldId}
              disabled={false}
              onImported={importPrescriptions}
            />
          </div>
        ) : (
          <>
            {/* TODO: Add section for existing boundary/prescriptions */}
            {isNonNil(errorMessage) && <div className="text-red-500">{errorMessage}</div>}
            {createBoundaryAsset.isError && (
              <div className="text-red-500">{createAssetErrorMessage}</div>
            )}
            <input
              type="file"
              id="geospatial-file"
              accept={ACCEPTED_FILE_EXTENSIONS_FOR_INPUT}
              multiple
              hidden
              onChange={processUploadedFiles}
            />
            {uploadedFiles.length > 0 ? (
              <div className="rounded-lg border p-4">
                <Form.Label htmlFor="asset-name">{t(($) => $.labels.name)}</Form.Label>
                <Form.Input
                  id="asset-name"
                  type="text"
                  className="w-full"
                  placeholder={stripFileExtension(uploadedFiles[0].name)}
                  onChange={(e) => setAssetName(e.target.value)}
                />
              </div>
            ) : (
              <div className="flex flex-col items-center gap-y-4 rounded-lg border p-4">
                {existingAssetPicker}
                {t(($) => $.or)}
                <Button
                  as="label"
                  htmlFor="geospatial-file"
                  className="h-10 cursor-pointer"
                  icon={SpriteMap.UploadIcon}
                >
                  {t(($) => $.fileUpload.chooseFile, {
                    count: 1
                  })}
                </Button>
              </div>
            )}
          </>
        )}

        <div className="flex gap-x-4">
          <Button
            kind={ButtonKind.Secondary}
            type="reset"
            onClick={closeFilePlanAssetModal}
            className="my-2 flex-1"
          >
            {t(($) => $.buttons.cancel)}
          </Button>
          {offersSave && (
            <Button
              onClick={() => confirmThenSave()}
              className="my-2 flex-1"
              disabledWhen={{
                when: uploadedFiles.length === 0 && isNil(selectedExistingAsset),
                reason: t(($) => $.planning.chooseFileFirst)
              }}
            >
              {t(($) => $.buttons.saveAndClose)}
            </Button>
          )}
        </div>
      </div>
      <ConfirmAdditionalBoundaryModal
        show={showBoundaryConfirm}
        existingCount={existingBoundariesForField.data?.length ?? 0}
        onCreateAnother={() => {
          setShowBoundaryConfirm(false)
          saveAssets(pendingFiles)
        }}
        onReplace={replaceBoundaryThenSave}
        onCancel={() => setShowBoundaryConfirm(false)}
        pending={deleteBoundaryAsset.isPending || createBoundaryAsset.isPending}
        error={
          deleteBoundaryAsset.isError
            ? t(($) => $.planning.boundaryCreation.confirmAdditional.replaceFailed)
            : undefined
        }
      />
    </Modal>
  )
}

export const Plan = ({
  planDataAndEditing,
  plansDataAndEditingSetter,
  anotherPlanBeingEdited,
  fieldId,
  disabledButtons,
  mapPage,
  onClick
}: {
  planDataAndEditing: { editing: boolean; data: FrontendPlan }
  plansDataAndEditingSetter: Dispatch<SetStateAction<{ editing: boolean; data: FrontendPlan }[]>>
  anotherPlanBeingEdited: boolean
  fieldId: string
  disabledButtons: boolean
  mapPage?: true
  onClick?: () => void
}) => {
  const showGuidance = useFeatureFlag(FeatureFlag.Guidance)

  const plan = planDataAndEditing.data
  const isEditing = planDataAndEditing.editing
  const [filePlanAssetModalType, setFilePlanAssetModalType] =
    useState<FilePlanAssetModalType>(undefined)

  const [taskErrorMessage, setTaskErrorMessage] = useState<string | null>(null)

  // TODO: Replace state with form
  const id = plan.id
  const [name, setName] = useState(plan.name)
  const [task, setTask] = useState(plan.task)
  const [date, setDate] = useState(plan.date)
  const [crop, setCrop] = useState(plan.crop)
  const [customCrop, setCustomCrop] = useState(plan.customCrop ?? '')
  const [customCropError, setCustomCropError] = useState<string | null>(null)
  const [boundary, setBoundary] = useState(plan.boundary)
  const [prescriptions, setPrescriptions] = useState(plan.prescriptions)
  const [guidance, setGuidance] = useState(plan.guidance)

  const planCreator = useCreatePlan()
  const planUpdater = useUpdatePlan()
  const planDeleter = useDeletePlans()
  const dispatchPlanningModalsState = usePlanningModalsDispatchContext()
  const { t, locale } = useLocalization()
  const [today] = useState(() => new Date())

  if (isNil(id)) return

  const dateSelectFormatter = new Intl.DateTimeFormat(locale, {
    month: 'long',
    year: 'numeric'
  })
  const shortMonthFormatter = new Intl.DateTimeFormat(locale, {
    month: 'short',
    year: 'numeric'
  })
  const thisYear = today.getFullYear()
  const thisMonth = today.getMonth()

  const formatTaskName = (name: Task) => t(($) => $.labels.tasks[name])

  const updatePlansDataAndEditingForPlan = ({
    editing,
    newData
  }: {
    editing: boolean
    newData?: FrontendPlan
  }) => {
    plansDataAndEditingSetter((existingPlans) => {
      const thisPlanIndex = existingPlans.findIndex(
        (existingPlan) => plan.id === existingPlan.data.id
      )
      return existingPlans.toSpliced(thisPlanIndex, 1, {
        editing,
        data: newData ?? existingPlans[thisPlanIndex].data
      })
    })
  }

  const editDisabled = disabledButtons || anotherPlanBeingEdited
  const startEditing = () => {
    if (isNonNil(onClick)) onClick()
    else updatePlansDataAndEditingForPlan({ editing: true })
  }

  const handleTaskSelection = (value: Task) => {
    setTaskErrorMessage('')
    setTask(value)
    if (
      isNil(name) ||
      name === '' ||
      taskSchema.options.map((task) => formatTaskName(task)).includes(name)
    )
      setName(formatTaskName(value))
  }

  const cancelEdit = () => {
    if (id.startsWith('newPlan'))
      plansDataAndEditingSetter((existingPlans) =>
        existingPlans.filter((existingPlan) => existingPlan.data.id !== id)
      )
    else {
      setName(plan.name)
      setTask(plan.task)
      setDate(plan.date)
      setCrop(plan.crop)
      setCustomCrop(plan.customCrop ?? '')
      setBoundary(plan.boundary)
      setPrescriptions(plan.prescriptions)
      setGuidance(plan.guidance)

      updatePlansDataAndEditingForPlan({ editing: false })
    }
  }

  const saveAssetsToPlan = (
    assets: {
      id: string
      name: string
      type?: ControllableApplication | GuidancePrimitive
    }[]
  ) => {
    if (filePlanAssetModalType === Mode.Prescription && assets.every(({ type }) => isNonNil(type)))
      setPrescriptions((currentPrescriptions) => [
        ...currentPrescriptions,
        ...(assets as {
          id: string
          name: string
          type: ControllableApplication
        }[])
      ])
    else setBoundary(assets[0])
  }

  const savePlan = () => {
    // TODO: Consider putting this in a Form and using react-hook-form validation to display error messages
    if (isNil(task)) {
      setTaskErrorMessage(t(($) => $.planning.taskRequired))
      return
    }
    if (crop === CropType.Other && customCrop.trim() === '') {
      setCustomCropError(t(($) => $.planning.enterCropName))
      return
    }
    if (isNil(boundary) && prescriptions.length === 0) {
      dispatchPlanningModalsState({
        type: ModalActionOption.InvalidPlanOpen,
        value: true
      })
      dispatchPlanningModalsState({
        type: ModalActionOption.ActionHandler,
        value: () => deletePlan()
      })
      return
    }

    // TODO: Fix type (create vs update)
    // TODO: Figure out best way to send updated data to setter in case of update
    const onSuccess = (data: FrontendPlan) => {
      setBoundary(data.boundary)
      setPrescriptions(data.prescriptions)
      setGuidance(data.guidance)
      // TODO: Replace data with validated form data?
      updatePlansDataAndEditingForPlan({
        editing: false,
        newData: {
          ...data,
          id: data.id ?? plan.id
        }
      })
    }

    const mutationBody = {
      fieldId,
      name: name ?? formatTaskName(task),
      task,
      date,
      crop,
      customCrop: crop === CropType.Other ? customCrop : undefined,
      boundary,
      prescriptions,
      tankMixes: [],
      hybridInputs: [],
      guidance
    } satisfies Omit<Locutus.Plan, 'id' | 'orgCode' | 'lastModified'>

    // TODO: Just always send mutationBody?
    if (id.startsWith('newPlan'))
      planCreator.mutate(mutationBody, {
        onSuccess: (data) => onSuccess(data)
      })
    else {
      planUpdater.mutate(
        { ...mutationBody, id },
        {
          onSuccess: () => onSuccess(mutationBody)
        }
      )
    }
  }

  const deletePlan = () => {
    const updateUI = () => {
      plansDataAndEditingSetter((existingPlans) =>
        existingPlans.filter((existingPlan) => existingPlan.data.id !== id)
      )
    }

    if (id.startsWith('newPlan')) updateUI()
    else
      planDeleter.mutate(
        {
          fieldId,
          planIds: [id]
        },
        {
          onSuccess: updateUI
        }
      )
  }

  const assetsToDisplay = [
    ...(isNonNil(crop)
      ? [
          {
            id: 'crop',
            name: translateCropKey({ cropKey: resolveCropKey({ crop, customCrop }), t }),
            type: 'crop'
          } as const
        ]
      : []),
    ...(isNonNil(boundary) ? [{ ...boundary, type: 'boundary' } as const] : []),
    ...prescriptions.map(
      (rx) =>
        ({
          ...rx,
          type: 'prescription'
        }) as const
    ),
    ...(showGuidance && isNonNil(guidance) ? [{ ...guidance.plan, type: 'guidance' } as const] : [])
  ] satisfies PlanAssetChipType[]

  // Month and year combinations for the next 24 months
  const dateSelectOptions = numsFromTo(0, 24).map((i) => ({
    month: (thisMonth + i) % 12,
    year: thisYear + Math.floor((i + thisMonth) / 12)
  }))

  const dateSelectValue =
    date.type === 'expiring' ? { month: date.month, year: date.year } : undefined

  const dateSelectOnChange = (date: MonthAndYear | undefined) =>
    setDate(
      date === undefined
        ? { type: 'recurring' }
        : { type: 'expiring', month: date.month, year: date.year }
    )

  return (
    <div className="group/plan relative last:rounded-b-lg">
      {isEditing ? (
        <>
          <div
            className={planEditingHeaderClasses({ sticky: isNil(mapPage) })}
            style={{
              top: `${BOUNDING_TOP_PX + FARM_HEADER_HEIGHT_PX + FIELD_HEADER_HEIGHT_PX}px`
            }}
          >
            <div className="flex items-center gap-x-2">
              <Icon icon={SpriteMap.EditIcon} className="size-4" />
              <span className="pan-title5">
                {t(($) => $.planning[id.startsWith('newPlan') ? 'creatingPlan' : 'editingPlan'])}
              </span>
            </div>
            <MeatballMenu menuItemsWidth="w-40" color="text-white">
              <MeatballMenuItem
                title={t(($) => $.planning.deletePlan)}
                icon={<Icon icon={SpriteMap.TrashIcon} className="size-6" />}
                color="danger"
                onClick={deletePlan}
                disabled={disabledButtons}
              />
            </MeatballMenu>
          </div>
          <div className="flex flex-col gap-y-3 border border-green bg-white p-3 group-last/plan:rounded-b-lg">
            <div className="w-full">
              <Form.Label htmlFor="plan-name">{t(($) => $.planning.planName)}</Form.Label>
              <Form.Input
                type="text"
                id="plan-name"
                className="mb-0 w-full"
                onChange={(e) => setName(e.target.value)}
                value={name}
              />
            </div>

            <div>
              {isNonNil(taskErrorMessage) && <div className="text-red-500">{taskErrorMessage}</div>}
              <PanoramaSelectMenu
                size="fill"
                label={t(($) => $.agConcepts.task)}
                onChange={handleTaskSelection}
                options={taskSchema.options}
                nameFormatter={(name: Task) => (
                  <div className="flex items-center gap-x-2.5 text-inherit">
                    <TaskIcon task={name} />
                    {formatTaskName(name)}
                  </div>
                )}
                value={task}
              />
            </div>

            <PanoramaSelectMenu
              size="fill"
              label={t(($) => $.labels.date) + ` ${t(($) => $.optional)}`}
              options={dateSelectOptions}
              value={dateSelectValue}
              nameAccessor={(date: MonthAndYear) =>
                dateSelectFormatter.format(new Date(date.year, date.month, 1))
              }
              idAccessor={({ month, year }) => `${month}#${year}`}
              onChange={dateSelectOnChange}
              clearable
            />

            <PanoramaSelectMenu
              size="fill"
              label={t(($) => $.agConcepts.crop) + ` ${t(($) => $.optional)}`}
              onChange={(value) => {
                setCrop(value)
                if (value !== CropType.Other) setCustomCrop('')
              }}
              options={Object.values(CropType)}
              value={crop}
              nameAccessor={(name) => t(($) => $.crops[name])}
              clearable
            />
            {crop === CropType.Other && (
              <>
                <Form.Input
                  type="text"
                  placeholder={t(($) => $.labels.cropName)}
                  className="mb-0 w-full pt-2.5"
                  value={customCrop}
                  onChange={(e) => {
                    setCustomCrop(e.target.value)
                    setCustomCropError(null)
                  }}
                />
                {isNonNil(customCropError) && (
                  <div className="mt-1 text-sm text-red-500">{customCropError}</div>
                )}
              </>
            )}

            <div>
              <span className="pan-label mb-2">{t(($) => $.planning.boundary)}</span>
              <div className="flex items-center gap-x-3">
                {isNil(boundary) ? (
                  <Button
                    className="h-8 w-fit cursor-pointer rounded-full py-3"
                    onClick={() => setFilePlanAssetModalType(Mode.Boundary)}
                    icon={SpriteMap.AddCircleIcon}
                  >
                    {t(($) => $.buttons.add)}
                  </Button>
                ) : (
                  <PlanAssetChip
                    planAsset={{
                      id: boundary.id,
                      name: boundary.name,
                      type: 'boundary'
                    }}
                    onRemove={() => setBoundary(undefined)}
                  />
                )}
              </div>
            </div>

            <div>
              <span className="pan-label mb-2">{t(($) => $.planning.prescriptions)}</span>
              <div className="flex flex-wrap items-center gap-2">
                {prescriptions.length > 0 &&
                  prescriptions.map((prescription) => (
                    <PlanAssetChip
                      key={prescription.id}
                      planAsset={{
                        id: prescription.id,
                        name: prescription.name,
                        type: 'prescription'
                      }}
                      onRemove={() =>
                        setPrescriptions((existingPrescriptions) =>
                          existingPrescriptions.filter(
                            (existingPrescription) => existingPrescription.id !== prescription.id
                          )
                        )
                      }
                    />
                  ))}
                <Button
                  className="h-8 w-fit cursor-pointer rounded-full py-3"
                  onClick={() => setFilePlanAssetModalType(Mode.Prescription)}
                  icon={SpriteMap.AddCircleIcon}
                >
                  {t(($) => $.buttons.add)}
                </Button>
              </div>
            </div>

            {showGuidance && guidance !== undefined && (
              <div>
                <span className="pan-label mb-2">{t(($) => $.planning.guidance)}</span>
                <PlanAssetChip
                  planAsset={{ ...guidance.plan, type: 'guidance' }}
                  onRemove={() => setGuidance(undefined)}
                />
              </div>
            )}

            <div className="flex w-full flex-wrap gap-3">
              <Button kind={ButtonKind.Secondary} className="flex-1" onClick={cancelEdit}>
                {t(($) => $.buttons.cancel)}
              </Button>
              <Button className="flex-1" onClick={savePlan}>
                {t(($) => $.buttons.save)}
              </Button>
            </div>
          </div>
          <FilePlanAssetUploader
            fieldId={fieldId}
            processSavedAssets={saveAssetsToPlan}
            modalState={[filePlanAssetModalType, setFilePlanAssetModalType]}
          />
        </>
      ) : (
        <div className={planCardClasses({ mapPage: mapPage === true, disabled: editDisabled })}>
          <div className="flex min-w-0 items-center gap-x-2 pr-4">
            {isNonNil(task) && <TaskIcon task={task} className="shrink-0" />}
            <span className="pan-detail-bold min-w-0 break-words text-base">{name}</span>
            {date.type === 'expiring' && (
              <span className="pan-label-wide text-gray-500">
                {shortMonthFormatter.format(new Date(date.year, date.month, 1))}
              </span>
            )}
          </div>
          <div className="flex min-w-0 flex-wrap gap-2 pr-6">
            {assetsToDisplay.map((planAsset) => (
              <PlanAssetChip key={planAsset.id} planAsset={planAsset} />
            ))}
          </div>
          <div className="absolute right-4 top-4 text-gray-300">
            <MeatballMenu menuItemsWidth="w-40">
              <MeatballMenuItem
                title={t(($) => $.buttons.edit)}
                icon={<Icon icon={SpriteMap.EditIcon} className="size-6" />}
                onClick={startEditing}
                disabled={editDisabled}
              />
              <MeatballMenuItem
                title={t(($) => $.planning.deletePlan)}
                icon={<Icon icon={SpriteMap.TrashIcon} className="size-6" />}
                color="danger"
                onClick={deletePlan}
                disabled={disabledButtons}
              />
            </MeatballMenu>
          </div>
        </div>
      )}
    </div>
  )
}

const Field = ({
  field,
  disabledButtons,
  ...props
}: {
  field: {
    id: string
    name: string
    plans: FrontendPlan[]
  }
  disabledButtons: boolean
  deleteCff: DeleteCff
}) => {
  const [plansDataAndEditing, setPlansDataAndEditing] = useState(
    field.plans.map((plan) => ({ editing: false, data: plan }))
  )
  const { t } = useLocalization()

  const addNewPlan = () => {
    setPlansDataAndEditing((existingPlans) => [
      {
        editing: true,
        data: {
          ...getInitialPlan(),
          // This is so each field can have exactly one new (unsaved) plan at a time
          id: `newPlan#${field.id}`,
          fieldId: field.id
        }
      },
      ...existingPlans
    ])
  }

  const hasPlanBeingEdited = plansDataAndEditing.some(({ editing }) => editing === true)

  return (
    <div className="flex min-w-0 flex-col justify-between rounded-lg" key={field.name}>
      <div
        className="z-[2] overflow-visible bg-gray-50 md:sticky"
        style={{
          top: `${BOUNDING_TOP_PX + FARM_HEADER_HEIGHT_PX}px`,
          minHeight: `${FIELD_HEADER_HEIGHT_PX}px`
        }}
      >
        <div className="flex min-h-10 min-w-0 flex-wrap items-center justify-between gap-x-2 gap-y-1 overflow-visible rounded-t-lg bg-gray-900 px-3 py-2 text-white">
          <div className="flex min-w-32 flex-1 items-center gap-x-2">
            <span className={`pan-body-bold ${treeNameClasses}`}>{field.name}</span>
            <span className="pan-body whitespace-nowrap text-green-300">
              {t(($) => $.planning.numPlans, {
                count: plansDataAndEditing.filter(
                  (plan) => plan.data.id?.startsWith('newPlan') === false
                ).length
              })}
            </span>
          </div>
          <div className={treeHeaderActionsClasses}>
            <button
              className="pan-label-wide flex shrink-0 items-center whitespace-nowrap uppercase disabled:cursor-not-allowed disabled:opacity-50"
              onClick={addNewPlan}
              disabled={hasPlanBeingEdited || disabledButtons}
            >
              {t(($) => $.planning.newPlan)}
              <Icon icon={SpriteMap.AddCircleIcon} className="ml-1 size-4 shrink-0" />
            </button>
            <MeatballMenu menuItemsWidth="w-40" color="text-gray-50">
              <MeatballMenuItem
                title={t(($) => $['delete-field'])}
                icon={<Icon icon={SpriteMap.TrashIcon} className="size-6" />}
                color="danger"
                onClick={() => props.deleteCff({ type: 'field', field })}
                disabled={disabledButtons}
              />
            </MeatballMenu>
          </div>
        </div>
      </div>
      {plansDataAndEditing.length > 0 ? (
        plansDataAndEditing.map((planDataAndEditing) => (
          <Plan
            key={planDataAndEditing.data.id}
            planDataAndEditing={planDataAndEditing}
            plansDataAndEditingSetter={setPlansDataAndEditing}
            anotherPlanBeingEdited={hasPlanBeingEdited}
            fieldId={field.id}
            disabledButtons={disabledButtons}
          />
        ))
      ) : (
        <div className="rounded-b-lg bg-white p-4">{t(($) => $.planning.noPlans)}</div>
      )}
    </div>
  )
}

const Client = ({
  client,
  openCffModal,
  deleteCff,
  isDefaultOpen,
  disabledButtons
}: {
  client: PlanningClient
  openCffModal: OpenCffModal
  deleteCff: DeleteCff
  isDefaultOpen: boolean
  disabledButtons: boolean
}) => {
  const { t } = useLocalization()

  const firstFarmName = client.farms.at(0)?.name
  const lastFarmName = client.farms.at(-1)?.name

  const clientRef = useRef<HTMLDivElement | null>(null)
  const connectorRef = useRef<HTMLDivElement | null>(null)
  const farmIconRefs = useRef(new Map<string, HTMLElement>())
  useSyncOnLayoutChange(
    () => {
      const connector = connectorRef.current
      if (!connector) return
      const firstFarmIcon =
        firstFarmName === undefined ? undefined : farmIconRefs.current.get(firstFarmName)
      const rowLayout = window.matchMedia(TREE_ROW_LAYOUT_MEDIA_QUERY).matches
      if (rowLayout) {
        connector.style.height = ''
      } else if (firstFarmIcon !== undefined) {
        const iconRect = firstFarmIcon.getBoundingClientRect()
        const top = connector.getBoundingClientRect().top
        const height = iconRect.top + iconRect.height / 2 - top
        connector.style.height = height > 0 ? `${height}px` : ''
      }
      const rect = connector.getBoundingClientRect()
      if (rect.height === 0 || rect.bottom < 0 || rect.top > window.innerHeight) return
      let inset = 0
      let lastFarmDy: number | null = null
      farmIconRefs.current.forEach((icon, name) => {
        const iconRect = icon.getBoundingClientRect()
        const dy = rect.bottom - (iconRect.top + iconRect.height / 2)
        if (name === lastFarmName) lastFarmDy = dy
        if (Math.abs(dy) < FARM_ICON_RADIUS_PX) {
          inset = Math.max(inset, Math.sqrt(FARM_ICON_RADIUS_PX ** 2 - dy ** 2))
        }
      })
      connector.style.width = `${CLIENT_CONNECTOR_MAX_WIDTH_PX - inset}px`
      const detached =
        client.farms.length === 0 || (lastFarmDy !== null && lastFarmDy > FARM_ICON_RADIUS_PX)
      connector.style.opacity = detached ? '0' : '1'
    },
    // Observe the client so the connector is recomputed when it opens/collapses or a farm is
    // toggled — layout changes that move the farm icons but fire no scroll event.
    { observe: clientRef }
  )

  // TODO: error handling -- backend error reporting could also be improved
  // 1. file upload errors
  // 2. delete errors
  // 3. create errors

  return (
    <Disclosure as="div" ref={clientRef} className="overflow-visible" defaultOpen={isDefaultOpen}>
      <DisclosureButton
        as="div"
        className={`group z-[4] cursor-default select-none overflow-visible bg-transparent p-4 md:sticky md:bg-gray-50 ${treeHeaderClasses}`}
        style={{
          minHeight: `${CLIENT_HEADER_HEIGHT_PX}px`,
          top: `${OPERATION_LAYOUT_CONTENT_TO_NAV_BAR_PX}px`
        }}
      >
        <div className="relative shrink-0">
          <div className={iconBorderClasses()}>
            <ClientIcon />
          </div>
          <div
            ref={connectorRef}
            className="absolute left-4 top-full z-0 h-8 border-b border-l border-green ui-not-open:hidden"
            style={{ width: `${CLIENT_CONNECTOR_MAX_WIDTH_PX - FARM_ICON_RADIUS_PX}px` }}
          />
        </div>
        <div className={treeHeaderBodyClasses}>
          <div className={treeHeaderTitleClasses}>
            <ChevronDownIcon className={disclosureChevronClasses()} />
            <p className={`pan-title2 ${treeNameClasses}`}>{client.name}</p>
            <p className="pan-body whitespace-nowrap text-green-500">
              {t(($) => $['num-farms'], {
                count: client.farms.length
              })}
            </p>
          </div>
          <div className={treeHeaderActionsClasses}>
            <button
              type="button"
              className={addFarmFieldButtonClasses()}
              disabled={disabledButtons}
              onClick={(e) => {
                e.stopPropagation() // needed to prevent the disclosure from toggling
                openCffModal({ client: client.name })
              }}
            >
              {t(($) => $['add-farm'])}
              <Icon icon={SpriteMap.AddCircleIcon} className="ml-1 size-5 shrink-0" />
            </button>
            <MeatballMenu menuItemsWidth="w-40">
              <MeatballMenuItem
                title={t(($) => $['delete-client'])}
                icon={<Icon icon={SpriteMap.TrashIcon} className="size-6" />}
                color="danger"
                onClick={() => deleteCff({ type: 'client', client })}
                disabled={disabledButtons}
              />
            </MeatballMenu>
          </div>
        </div>
      </DisclosureButton>
      <DisclosurePanel className="overflow-visible">
        <div className="relative mb-4 flex min-w-0 flex-col gap-4 overflow-visible pl-16 pr-4">
          <div className="absolute left-20 hidden h-full border-l border-green md:block" />
          {client.farms.map((farm, farmIndex) => (
            <Disclosure
              as="div"
              className="relative z-0 min-w-0 md:last:bg-gray-50"
              key={farm.name}
              defaultOpen={isDefaultOpen}
            >
              <FarmTreeSpine farmIndex={farmIndex} farmCount={client.farms.length} />
              <DisclosureButton
                as="div"
                className={`group cursor-default select-none bg-transparent md:sticky md:z-[3] md:bg-gray-50 ${treeHeaderClasses}`}
                style={{
                  top: `${BOUNDING_TOP_PX}px`,
                  minHeight: `${FARM_HEADER_HEIGHT_PX}px`
                }}
              >
                <div
                  className={iconBorderClasses({ className: 'relative z-10' })}
                  ref={(el) => {
                    if (el) farmIconRefs.current.set(farm.name, el)
                    else farmIconRefs.current.delete(farm.name)
                  }}
                >
                  <FarmIcon />
                </div>
                <div className={treeHeaderBodyClasses}>
                  <div className={treeHeaderTitleClasses}>
                    <ChevronDownIcon className={disclosureChevronClasses()} />
                    <p className={`pan-title4 ${treeNameClasses}`}>{farm.name}</p>
                    <p className="pan-body whitespace-nowrap text-green-500">
                      {t(($) => $['num-fields'], {
                        count: farm.fields.length
                      })}
                    </p>
                  </div>
                  <div className={treeHeaderActionsClasses}>
                    <button
                      type="button"
                      className={addFarmFieldButtonClasses()}
                      disabled={disabledButtons}
                      onClick={(e) => {
                        e.stopPropagation() // needed to prevent the disclosure from toggling
                        openCffModal({
                          client: client.name,
                          farm: farm.name
                        })
                      }}
                    >
                      {t(($) => $['add-field'])}
                      <Icon icon={SpriteMap.AddCircleIcon} className="ml-1 size-5 shrink-0" />
                    </button>
                    <MeatballMenu menuItemsWidth="w-40">
                      <MeatballMenuItem
                        title={t(($) => $['delete-farm'])}
                        icon={<Icon icon={SpriteMap.TrashIcon} className="size-6" />}
                        color="danger"
                        onClick={() => deleteCff({ type: 'farm', farm })}
                        disabled={disabledButtons}
                      />
                    </MeatballMenu>
                  </div>
                </div>
              </DisclosureButton>
              <DisclosurePanel>
                <div className="flex min-w-0 flex-col gap-3 pl-16">
                  {farm.fields.map((field) => (
                    <Field
                      key={field.id}
                      field={field}
                      deleteCff={deleteCff}
                      disabledButtons={disabledButtons}
                    />
                  ))}
                </div>
              </DisclosurePanel>
            </Disclosure>
          ))}
        </div>
      </DisclosurePanel>
    </Disclosure>
  )
}

const checkboxClasses = createVariants({
  base: 'flex items-center justify-center size-4 shrink-0 rounded-sm border transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-50',
  variants: {
    selected: {
      true: 'bg-green-500 border-green-500',
      false: 'bg-white border-gray-300'
    }
  }
})

const checkboxSlotClasses = createVariants({
  base: 'relative z-10 flex h-8 w-4 shrink-0 items-center justify-center bg-white',
  variants: {
    onBranch: {
      true: 'ml-1',
      false: ''
    }
  }
})

type CheckValue = boolean | 'mixed'

type UncopiedFarm = {
  name: string
  fields: Cff[]
}

type UncopiedClient = {
  name: string
  farms: UncopiedFarm[]
}

const matchesSearch = (field: Cff, search: string): boolean =>
  `${field.client} ${field.farm} ${field.field}`.toLowerCase().includes(search.toLowerCase())

const groupUncopiedFields = (fields: readonly Cff[]): UncopiedClient[] => {
  const sorted = fields.toSorted(
    (a, b) =>
      a.client.localeCompare(b.client) ||
      a.farm.localeCompare(b.farm) ||
      a.field.localeCompare(b.field)
  )
  return groupAndReduce(sorted, {
    getGroupingKey: (field) => field.client,
    handleFirst: (field) => ({
      name: field.client,
      farms: [{ name: field.farm, fields: [field] }]
    }),
    accumulateRest: (client, field) => {
      const farm = client.farms.find((item) => item.name === field.farm)
      if (farm === undefined) {
        return {
          ...client,
          farms: [...client.farms, { name: field.farm, fields: [field] }]
        }
      }
      return {
        ...client,
        farms: client.farms.map((item) =>
          item.name === field.farm ? { ...item, fields: [...item.fields, field] } : item
        )
      }
    }
  })
}

const checkValue = (keys: readonly string[], selected: ReadonlySet<string>): CheckValue => {
  if (keys.length === 0) return false
  const selectedCount = keys.filter((key) => selected.has(key)).length
  if (selectedCount === 0) return false
  if (selectedCount === keys.length) return true
  return 'mixed'
}

const addKeys = (selected: ReadonlySet<string>, keys: readonly string[]): Set<string> => {
  const next = new Set(selected)
  for (const key of keys) next.add(key)
  return next
}

const toggleKeys = (selected: ReadonlySet<string>, keys: readonly string[]): Set<string> => {
  const next = new Set(selected)
  const allSelected = keys.length > 0 && keys.every((key) => selected.has(key))
  if (allSelected) {
    for (const key of keys) next.delete(key)
  } else {
    for (const key of keys) next.add(key)
  }
  return next
}

const keysOf = (fields: readonly Cff[]): string[] => fields.map((field) => cffKey(field))

const useUncopiedPanoramaFields = (): {
  isSettled: boolean
  fields: Cff[]
} => {
  const streams = useAllStreams()
  const planningFields = usePlanningCffItems()
  const isSettled = streams.isSuccess && planningFields.isSuccess
  if (!isSettled) return { isSettled: false, fields: [] }

  return {
    isSettled: true,
    fields: browsableFields({
      streams: streams.data,
      planningFields: planningFields.data
    }).flatMap(({ planningFieldId, client, farm, field }) =>
      planningFieldId === undefined ? { client, farm, field } : []
    )
  }
}

const treeThroughClasses =
  'pointer-events-none absolute inset-y-0 left-0 -translate-x-px border-l border-green'
const treeDropClasses =
  'pointer-events-none absolute bottom-0 left-10 top-1/2 -translate-x-px border-l border-green'
const treeBranchClasses = 'pointer-events-none absolute inset-y-0 left-0 w-10 -translate-x-px'
const treeBranchStemClasses = createVariants({
  base: 'absolute left-0 border-l border-green',
  variants: {
    isLast: {
      true: 'top-0 h-1/2',
      false: 'inset-y-0'
    }
  }
})
const treeBranchArmClasses = 'absolute left-0 top-1/2 w-10 border-b border-green'

const expandToggleChevronClasses = createVariants({
  base: 'shrink-0 transition-transform',
  variants: {
    expanded: {
      true: 'rotate-0',
      false: 'rotate-180'
    }
  }
})

const TriStateCheckbox = ({
  value,
  disabled,
  onToggle,
  label
}: {
  value: CheckValue
  disabled: boolean
  onToggle: () => void
  label: string
}) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={value}
    aria-label={label}
    disabled={disabled}
    onClick={onToggle}
    className={checkboxClasses({ selected: value !== false })}
  >
    {value === true && (
      <svg className="size-3" viewBox="0 0 10 8" fill="none" aria-hidden="true">
        <path
          d="M1 4L3.5 6.5L9 1"
          stroke="white"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )}
    {value === 'mixed' && <span className="h-0.5 w-2.5 rounded-sm bg-white" />}
  </button>
)

const UncopiedPanoramaFields = ({
  search,
  disabledWhen,
  isSettled,
  fields,
  preselectAll
}: {
  search: string
  disabledWhen: DisabledGuard
  isSettled: boolean
  fields: Cff[]
  preselectAll: boolean
}) => {
  const { t } = useLocalization()
  const orgCode = useOrgCodeOrThrow()
  const createPlanningCffs = usePanoramaMutation(createPlanningCffsApi)
  const [selected, setSelected] = useState<ReadonlySet<string>>(() =>
    preselectAll ? new Set(keysOf(fields)) : new Set()
  )
  const [isExpanded, setIsExpanded] = useState(preselectAll)

  if (!isSettled || fields.length === 0) return null

  const visible = fields.filter((field) => matchesSearch(field, search))
  const groups = groupUncopiedFields(visible)
  const visibleKeys = keysOf(visible)
  const selectedFields = fields.filter((field) => selected.has(cffKey(field)))
  const selectedCount = selectedFields.length
  const disabled = disabledWhen.when
  const addGuards: DisabledGuard[] = [
    disabledWhen,
    { when: selectedCount === 0, reason: t(($) => $.disabledReasons.selectFieldFirst) }
  ]

  const addSelected = () => {
    createPlanningCffs.mutate(
      {
        params: { orgCode },
        body: { fields: selectedFields }
      },
      {
        onSuccess: () => setSelected(new Set())
      }
    )
  }

  return (
    <div className={sectionCardClasses()}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="pan-title5 text-gray-900">
              {t(($) => $.planning.uncopiedPanoramaFields)}
            </h2>
            <span className="pan-body text-green">({fields.length})</span>
            {disabled && (
              <Popover icon="info" iconClassName="size-4 text-gray-600" side="bottom">
                <div className="max-w-xs rounded-md border border-gray-200 bg-white px-3 py-2 text-sm shadow-md">
                  {t(($) => $.planning.supporterCannotAdd)}
                </div>
              </Popover>
            )}
          </div>
          <p className="pan-body text-gray-600">
            {t(($) => $.planning.uncopiedPanoramaFieldsByline)}
          </p>
        </div>
        <button
          type="button"
          className="pan-body flex shrink-0 items-center gap-2 text-gray-600"
          onClick={() => setIsExpanded((current) => !current)}
        >
          <Icon
            icon={SpriteMap.ChevronIcon}
            className={expandToggleChevronClasses({ expanded: isExpanded, className: 'size-3' })}
          />
          {t(($) => (isExpanded ? $.buttons.collapse : $.buttons.expand))}
        </button>
      </div>
      {isExpanded && (
        <>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <TextButton
              type="button"
              disabledWhen={[
                disabledWhen,
                {
                  when: visibleKeys.length === 0,
                  reason: t(($) => $.disabledReasons.noFieldsToSelect)
                }
              ]}
              onClick={() => setSelected((current) => addKeys(current, visibleKeys))}
            >
              {t(($) => $.buttons.selectAll)}
            </TextButton>
            <TextButton
              type="button"
              className="text-gray-600 hover:text-gray-600 active:text-gray-600 aria-disabled:hover:text-gray-600"
              disabledWhen={[
                disabledWhen,
                { when: selectedCount === 0, reason: t(($) => $.disabledReasons.noFieldsSelected) }
              ]}
              onClick={() => setSelected(new Set())}
            >
              {t(($) => $.buttons.clear)}
            </TextButton>
            <span className="pan-body text-gray-600">
              {t(($) => $.planning.selectedCount, { count: selectedCount })}
            </span>
            <Button
              kind={ButtonKind.Primary}
              disabledWhen={addGuards}
              loading={createPlanningCffs.isPending}
              onClick={addSelected}
            >
              {t(($) => $.buttons.addSelected)}
            </Button>
          </div>
          {createPlanningCffs.isError && (
            <p className="mt-3 text-sm text-red-500">{createPlanningCffs.error.message}</p>
          )}
          {visible.length === 0 ? (
            <p className="mt-4 text-gray-600">{t(($) => $.planning.noMatchingUncopied)}</p>
          ) : (
            <div className="mt-4 flex flex-col gap-3">
              {groups.map((client) => {
                const clientKeys = client.farms.flatMap((farm) =>
                  farm.fields.map((field) => cffKey(field))
                )
                return (
                  <div key={client.name}>
                    <div className="relative flex min-h-8 min-w-0 items-center gap-2">
                      {client.farms.length > 0 && <div className={treeDropClasses} />}
                      <div className={checkboxSlotClasses()}>
                        <TriStateCheckbox
                          value={checkValue(clientKeys, selected)}
                          disabled={disabled}
                          label={client.name}
                          onToggle={() => setSelected((current) => toggleKeys(current, clientKeys))}
                        />
                      </div>
                      <div className={iconBorderClasses({ className: 'relative z-10' })}>
                        <ClientIcon />
                      </div>
                      <p className="pan-title2 min-w-0 break-words">{client.name}</p>
                    </div>
                    <div className="relative ml-10">
                      {client.farms.map((farm, farmIndex) => {
                        const farmKeys = keysOf(farm.fields)
                        const farmIsLast = farmIndex === client.farms.length - 1
                        return (
                          <div key={farm.name} className="relative">
                            {!farmIsLast && <div className={treeThroughClasses} />}
                            <div className="relative flex min-h-8 min-w-0 items-center gap-1">
                              <div className={treeBranchClasses}>
                                <div className={treeBranchStemClasses({ isLast: farmIsLast })} />
                                <div className={treeBranchArmClasses} />
                              </div>
                              {farm.fields.length > 0 && <div className={treeDropClasses} />}
                              <div className={checkboxSlotClasses({ onBranch: true })}>
                                <TriStateCheckbox
                                  value={checkValue(farmKeys, selected)}
                                  disabled={disabled}
                                  label={farm.name}
                                  onToggle={() =>
                                    setSelected((current) => toggleKeys(current, farmKeys))
                                  }
                                />
                              </div>
                              <div className={iconBorderClasses({ className: 'relative z-10' })}>
                                <FarmIcon />
                              </div>
                              <p className="pan-title4 min-w-0 break-words">{farm.name}</p>
                            </div>
                            <div className="relative ml-10">
                              {farm.fields.map((field, fieldIndex) => {
                                const key = cffKey(field)
                                const fieldIsLast = fieldIndex === farm.fields.length - 1
                                return (
                                  <div key={key} className="relative">
                                    {!fieldIsLast && <div className={treeThroughClasses} />}
                                    <div className="relative flex min-h-8 min-w-0 items-center gap-1">
                                      <div className={treeBranchClasses}>
                                        <div
                                          className={treeBranchStemClasses({ isLast: fieldIsLast })}
                                        />
                                        <div className={treeBranchArmClasses} />
                                      </div>
                                      <div className={checkboxSlotClasses({ onBranch: true })}>
                                        <TriStateCheckbox
                                          value={selected.has(key)}
                                          disabled={disabled}
                                          label={field.field}
                                          onToggle={() =>
                                            setSelected((current) => toggleKeys(current, [key]))
                                          }
                                        />
                                      </div>
                                      <div
                                        className={iconBorderClasses({
                                          className: 'relative z-10'
                                        })}
                                      >
                                        <PlanningFieldsIcon />
                                      </div>
                                      <p className="pan-body min-w-0 break-words">{field.field}</p>
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}
    </div>
  )
}

const FieldsContent = () => {
  const [searchParams] = useSearchParams()
  const selectUnregistered = searchParams.get(SELECT_UNREGISTERED_FIELDS_PARAM)
  const [existingCff, setExistingCff] = useState<{
    client?: string
    farm?: string
  }>({})
  const [search, setSearch] = useState('')
  const [isDefaultOpen, setIsDefaultOpen] = useState(false)
  const { t } = useLocalization()
  const orgInfo = useOrgInfo()
  const orgCode = useOrgCodeOrThrow()
  const createPlanningCffs = usePanoramaMutation(createPlanningCffsApi)
  const uncopiedPanoramaFields = useUncopiedPanoramaFields()
  const { mutate: downloadCffConfig } = useCffConfigFileDownload()
  const planningCffDeleter = useDeletePlanningCffs()
  const uploadCffs = usePanoramaMutation(convertFileToCffAPI)
  const planningCffs = usePlanningCffs({ search })
  const planningModalsState = usePlanningModalsContext()
  const dispatchPlanningModalsState = usePlanningModalsDispatchContext()
  const { reset, register, setValue, formState, handleSubmit, setFocus } = useForm<
    z.infer<typeof baseCffSchema>
  >({
    resolver: zodResolver(baseCffSchema)
  })

  if (planningCffs.isPending) return <Loading />

  const clients = planningCffs.data?.clients ?? []
  const isSupporter = isNonNil(orgInfo) ? OrgLevelPermissions.isSupporter(orgInfo) : false

  const closeCffModal = () => {
    dispatchPlanningModalsState({
      type: ModalActionOption.Close
    })
    reset()
    setTimeout(() => {
      setExistingCff({})
    }, 400)
  }

  const onCffSubmit = handleSubmit(
    // @ts-expect-error TODO: We know that this event is a submit event with an HTMLButton as the submitter, just need to figure out how to tell TS that
    (data, event: React.SyntheticEvent<HTMLButtonElement, SubmitEvent>) => {
      for (const key of Object.keys(data) as (keyof typeof data)[]) data[key] = data[key].trim()

      createPlanningCffs.mutate({
        params: {
          orgCode
        },
        body: {
          fields: [data]
        }
      })
      if ((event.nativeEvent.submitter as HTMLButtonElement).name === 'add-another') {
        setExistingCff({ client: data.client, farm: data.farm })
        reset({ client: data.client, farm: data.farm, field: '' })
        /**
         * reset() removes all input references, so this setTimeout gives
         * react hook form enough time to create new references before we try to
         * focus on one of them
         */
        setTimeout(() => setFocus('field'), 0)
      } else closeCffModal()
    }
  )

  const deleteCff: DeleteCff = async (cff: CffDeletion) => {
    if (cff.type === 'field') {
      planningCffDeleter.mutate({ cffIds: [cff.field.id] })
      return
    }

    if (cff.type === 'farm') {
      planningCffDeleter.mutate({ cffIds: cff.farm.fields.map(({ id }) => id) })
      return
    }

    planningCffDeleter.mutate({
      cffIds: cff.client.farms.flatMap(({ fields }) => fields.map(({ id }) => id))
    })
  }

  const openCffModal: OpenCffModal = ({ client, farm }) => {
    setExistingCff({ client, farm })
    setValue('client', client ?? '')
    setValue('farm', farm ?? '')
    dispatchPlanningModalsState({
      type: ModalActionOption.AddCFFOpen,
      value: true
    })
    // Give React time to render the modal and inputs before focusing on one
    setTimeout(() => setFocus(isNil(client) ? 'client' : isNil(farm) ? 'farm' : 'field'), 0)
  }

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file === undefined) return
    // Clear the input so choosing the same file again fires change
    e.target.value = ''

    uploadCffs.mutate({ params: { orgCode }, body: { file } })
  }

  const importDisabled = isSupporter || uploadCffs.isPending

  const isSearching = search !== ''
  const canAddFromAbove = uncopiedPanoramaFields.fields.length > 0
  const noFieldsMessage = canAddFromAbove
    ? t(($) => $.planning.noFieldsAddFromAbove)
    : t(($) => $.planning.noFields)
  const emptyFieldsMessage = isSearching ? t(($) => $.planning.noMatchingFields) : noFieldsMessage

  return (
    <div className="relative flex flex-col gap-8">
      {planningCffDeleter.isPending && (
        <div className="absolute -inset-4 z-20 rounded-lg bg-black/30">
          <Loading />
        </div>
      )}
      <div className="flex flex-wrap items-center">
        <div className="mb-2 flex w-full grow flex-wrap items-center gap-y-2 xl:mb-0 xl:w-fit">
          <h1 className="pan-billboard grow text-gray-900">
            {t(($) => $.agConcepts.field, { count: 2 })}
          </h1>
          <div className="relative w-full xl:mr-4 xl:w-fit">
            <Form.Input
              type="search"
              name="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t(($) => $.planning.searchFields)}
              className="mb-0 w-full border-0 px-3 leading-normal xl:w-64"
            />
            <Icon
              icon={SpriteMap.SearchIcon}
              className={`pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 cursor-text text-gray-600 ${search === '' ? '' : 'hidden'}`}
            />
          </div>
        </div>
        <div className="grid w-full grid-cols-2 gap-x-4 gap-y-2 md:flex xl:w-fit">
          <Button
            kind={ButtonKind.White}
            onClick={() => downloadCffConfig({ exportType: ExportType.Planned })}
            icon={SpriteMap.DownloadIcon}
            disabledWhen={{
              when: clients.length === 0,
              reason: t(($) => $.planning.noPlannedFieldsToExport)
            }}
          >
            {t(($) => $.buttons.download)}
          </Button>
          <div className="flex items-center gap-x-2">
            <Form.Input
              type="file"
              id="cff-file-input"
              accept={CFF_ACCEPTED_FILE_EXTENSIONS}
              hidden
              disabled={importDisabled}
              onChange={handleFileUpload}
            />
            <Button
              as="label"
              htmlFor="cff-file-input"
              kind={ButtonKind.White}
              className={importCffButtonClasses({ disabled: importDisabled })}
              aria-disabled={importDisabled}
              icon={SpriteMap.UploadIcon}
              loading={uploadCffs.isPending}
            >
              {t(($) => $.buttons.import)}
            </Button>
            <Popover icon="info" iconClassName="size-5 text-gray-600" side="bottom">
              <div className="max-w-xs rounded-md border border-gray-200 bg-white px-3 py-2 text-sm shadow-md">
                {t(($) => $.planning.uploadCffInfo, {
                  requiredColumns: 'Client, Farm, Field'
                })}
              </div>
            </Popover>
          </div>
          <Button
            kind={ButtonKind.White}
            onClick={() => openCffModal({})}
            icon={SpriteMap.AddCircleIcon}
            disabledWhen={{
              when: isSupporter,
              reason: t(($) => $.disabledReasons.supporterReadOnly)
            }}
          >
            {t(($) => $['add-field'])}
          </Button>
        </div>
        {uploadCffs.isError && (
          <p className="mt-2 w-full text-sm text-red-500">{uploadCffs.error.message}</p>
        )}
      </div>
      <UncopiedPanoramaFields
        key={`${uncopiedPanoramaFields.isSettled}|${selectUnregistered}`}
        preselectAll={selectUnregistered === 'all'}
        search={search}
        disabledWhen={{ when: isSupporter, reason: t(($) => $.disabledReasons.supporterReadOnly) }}
        isSettled={uncopiedPanoramaFields.isSettled}
        fields={uncopiedPanoramaFields.fields}
      />
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <span className="pan-title5 mr-3">{t(($) => $.agConcepts.field, { count: 2 })}</span>
            <span className="pan-body text-green">({planningCffs.data?.numFields ?? 0})</span>
          </div>
          <button
            className="flex items-center p-2"
            onClick={() => setIsDefaultOpen((currentValue) => !currentValue)}
          >
            <Icon
              icon={SpriteMap.ChevronIcon}
              className={expandToggleChevronClasses({
                expanded: isDefaultOpen,
                className: 'mr-2 size-3'
              })}
            />
            {t(($) => (isDefaultOpen ? $.buttons.collapseAll : $.buttons.expandAll))}
          </button>
        </div>
        <div className="flex flex-col divide-y" key={isDefaultOpen ? 1 : 0}>
          {clients.length > 0 ? (
            clients.map((client) => (
              <Client
                client={client}
                openCffModal={openCffModal}
                deleteCff={deleteCff}
                key={client.name}
                isDefaultOpen={isDefaultOpen}
                disabledButtons={isSupporter}
              />
            ))
          ) : (
            <div className="self-center">{emptyFieldsMessage}</div>
          )}
        </div>
      </div>
      <Modal
        title={
          isNil(existingCff.client)
            ? t(($) => $['add-client'])
            : isNil(existingCff.farm)
              ? t(($) => $['add-farm'])
              : t(($) => $['add-field'])
        }
        show={planningModalsState[ModalActionOption.AddCFFOpen] === true}
        onClose={closeCffModal}
      >
        <Form onSubmit={onCffSubmit} className="mt-4">
          {(['client', 'farm', 'field'] as const).map((inputField) => (
            <Fragment key={inputField}>
              <Form.Label htmlFor={inputField} errorMsg={formState.errors[inputField]?.message}>
                {t(($) => $.agConcepts[inputField], {
                  count: 1
                })}
              </Form.Label>
              <Form.Input required {...register(inputField)} />
            </Fragment>
          ))}
          <Form.SubmitButton type="submit" name="add-another" className="mb-2 mt-8">
            {t(($) => $.buttons.saveAndAddAnother)}
          </Form.SubmitButton>
          <div className="flex gap-x-4">
            <Button type="submit" className="my-2 w-1/2">
              {t(($) => $.buttons.saveAndClose)}
            </Button>
            <Button
              kind={ButtonKind.Secondary}
              type="reset"
              onClick={closeCffModal}
              className="my-2 w-1/2"
            >
              {t(($) => $.buttons.cancel)}
            </Button>
          </div>
        </Form>
      </Modal>
      <Modal
        title={t(($) => $.planning.invalidPlan)}
        show={planningModalsState[ModalActionOption.InvalidPlanOpen] === true}
        onClose={() =>
          dispatchPlanningModalsState({
            type: ModalActionOption.Close
          })
        }
      >
        <div className="my-4">
          {/* TODO: Update this translation once we can add products to plans */}
          {t(($) => $.planning.mustHaveAsset)}
        </div>
        <div className="flex gap-x-2">
          <Button
            kind={ButtonKind.Secondary}
            className="w-1/2"
            onClick={() =>
              dispatchPlanningModalsState({
                type: ModalActionOption.Close
              })
            }
          >
            {t(($) => $.buttons.goBack)}
          </Button>
          <Button
            kind={ButtonKind.Danger}
            className="w-1/2"
            onClick={() => {
              if (isNonNil(planningModalsState.actionHandler)) planningModalsState.actionHandler()
              dispatchPlanningModalsState({
                type: ModalActionOption.Close
              })
            }}
          >
            {t(($) => $.planning.deletePlan)}
          </Button>
        </div>
      </Modal>
    </div>
  )
}

export const Component = () => (
  <PlanningModalsProvider>
    <FieldsContent />
  </PlanningModalsProvider>
)

Component.displayName = 'Fields'
